import { defineStore } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { useBranchesStore } from '@/stores/branches';
import collect from 'collect.js';

export const useAssetsStore = defineStore('assets', {
    state: () => {
        return {
            assets: [],
            requested: false,
            payment_methods: null
        };
    },
    actions: {
        async loadAssets(refresh: boolean = false) {
            if (!this.requested || refresh) {
                this.assets = (await useAuthStore().request().get('api/assets')).data;
                // load payment methods
                await this.paymentMethods(true);

                this.requested = true;
            }

            return this.assets;
        },
        amountFormatted(amount: number | string, assetCode: string | null = null) {
            const asset = assetCode ? this.assets.find(asset => asset.code === assetCode) : this.default_asset;

            return Number(amount).toFixed(asset.decimal_places);
        },
        getDecimalPlaces(assetCode: string) {
            return this.assets.find(asset => asset.code === assetCode)?.decimal_places || 2;
        },
        getExchangeRate(assetCode: string, type: string = 'buy') {
            if (assetCode === this.default_asset.code) {
                return 1;
            }

            const asset = this.getAsset(assetCode);

            let exchangeRate = Number(type === 'buy' ? asset.exchange_rate_buy : asset.exchange_rate_sell);

            if (asset.exchange_rate_type === 'from') {
                exchangeRate = Number((1 / exchangeRate).toFixed(4));
            }

            return exchangeRate;
        },
        getAsset(assetCode: string) {
            return this.assets.find(asset => asset.code === assetCode);
        },
        getGoldKarats() {
            return this.assets.filter(asset => asset.type === 'gold' && asset.gold_type === 'new').map(asset => ({
                karat: asset.gold_karat,
                name: `عيار ${asset.gold_karat}`
            }));
        },
        allGoldKarats() {
            const branchesStore = useBranchesStore();

            const branchGoldKaratsPercentage = branchesStore.currentBranch?.gold_karats_percentage || {};

            return [
                { code: 'UAX_24', gold_karat: 24, name: 'عيار 24', percentage: branchGoldKaratsPercentage[24] ?? 100 },
                { code: 'UAX_22', gold_karat: 22, name: 'عيار 22', percentage: branchGoldKaratsPercentage[22] ?? 91.6 },
                { code: 'UAX_21', gold_karat: 21, name: 'عيار 21', percentage: branchGoldKaratsPercentage[21] ?? 87.5 },
                { code: 'UAX_18', gold_karat: 18, name: 'عيار 18', percentage: branchGoldKaratsPercentage[18] ?? 75 },
                { code: 'UAX_17', gold_karat: 17, name: 'عيار 17', percentage: branchGoldKaratsPercentage[17] ?? 70.83 },
                { code: 'UAX_14', gold_karat: 14, name: 'عيار 14', percentage: branchGoldKaratsPercentage[14] ?? 58.3 },
                { code: 'UAX_10', gold_karat: 10, name: 'عيار 10', percentage: branchGoldKaratsPercentage[10] ?? 41.7 }
            ];
        },
        convertGoldKarat(amount: number | string, fromKarat: number | string, toKarat: number | string) {
            fromKarat = typeof fromKarat === 'string' ? this.getAsset(fromKarat).gold_karat : fromKarat;
            toKarat = typeof toKarat === 'string' ? this.getAsset(toKarat).gold_karat : toKarat;

            amount = Number(amount);

            return amount * this.getGoldKaratPercentage(fromKarat) / this.getGoldKaratPercentage(toKarat);
        },
        getGoldKaratPercentage(goldKarat: number) {
            const configuredKarat = this.allGoldKarats().find(gk => gk.gold_karat === goldKarat);

            return configuredKarat ? Number(configuredKarat.percentage) / 100 : Number(goldKarat) / 24;
        },
        async paymentMethods(refresh: boolean = false) {
            if (refresh || !this.payment_methods) {
                this.payment_methods = (await useAuthStore().request().get('api/payment-methods')).data;
            }

            return this.payment_methods;
        },
        getPaymentMethod(paymentMethodCode: string) {
            return this.payment_methods.find(pm => pm.code === paymentMethodCode);
        },
        calculateTax(taxes: object[], selectedTaxIds: int[], amount: number) {
            return collect(taxes)
                .filter(tax => selectedTaxIds.includes(tax.id))
                .map(tax => {
                    let value = 0;

                    if (tax.percentage > 0) {
                        value += amount * Number(tax.percentage) / 100;
                    }

                    if (tax.amount > 0) {
                        value += Number(tax.amount);
                    }

                    return value;
                })
                .sum() || 0;
        },
        updateGoldPrices(prices: any) {
            this.assets.forEach(asset => {
                if (asset.type === 'gold' && prices[asset.gold_karat]) {
                    const globalPrice = Number(prices[asset.gold_karat]);
                    const buffer = Number(asset.auto_update_price_buffer || 0);
                    const finalPrice = (globalPrice + buffer).toFixed(asset.decimal_places || 2);
                    asset.exchange_rate_buy = finalPrice;
                    asset.exchange_rate_sell = finalPrice;
                }
            });
        }
    },
    getters: {
        default_asset: state => state.assets.find(asset => asset.is_default === true),
        golds: state => state.assets.filter(asset => asset.type === 'gold'),
        currencies: state => state.assets.filter(asset => asset.type === 'currency'),
        defaultPaymentMethod: state => state.payment_methods.find(pm => pm.is_default === true)
    }
});
